import { useEffect, useMemo, useState } from "react";
import { Outlet, useFetcher, useLoaderData } from "react-router";
import SidebarNav from "~/components/shared/desktop/SidebarNav";
import Footer from "~/components/shared/Footer";
import MobileHeader from "~/components/shared/mobile/Header";
import MobileMenu from "~/components/shared/mobile/Menu";
import LoadingIndicator from "~/components/shared/LoadingIndicator";
import ImageModalContext from "~/helpers/image-modal-context";
import { Button } from "~/components/shared/Button";
import { LoaderFunctionArgs } from "react-router";
import { getSession } from "~/session.server";
import { GetMyNotificationsQuery } from "~/@graphql/types";
import NotificationsDrawer from "~/components/shared/NotificationDrawer";

type LoaderData = {
  isLoggedIn: boolean;
};

export const loader = async (args: LoaderFunctionArgs) => {
  const session = await getSession(args.request, false);
  const token = session.get("accessToken");
  return {
    isLoggedIn: Boolean(token),
  };
};

export default function App() {
  const [isMenuOpen, toggleMenu] = useState<boolean>(false);
  const [isNotificationsPanelOpen, toggleToggleNotificationsPanel] =
    useState<boolean>(false);
  const [imageURL, setImageURL] = useState<string>();
  const { isLoggedIn } = useLoaderData<LoaderData>() as unknown as LoaderData;
  const notificationsFetcher = useFetcher<GetMyNotificationsQuery>();

  useEffect(() => {
    if (isLoggedIn) {
      notificationsFetcher.load("/profile/my-notifications");
    }
  }, [isLoggedIn]);

  const notifications =
    isLoggedIn && notificationsFetcher.data
      ? notificationsFetcher.data.party_notifications
      : null;

  const handleToggleMenu = () => {
    if (isMenuOpen) {
      document.getElementsByTagName("body")[0].classList.remove("no-scroll");
    } else {
      document.getElementsByTagName("body")[0].classList.add("no-scroll");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
    toggleMenu(!isMenuOpen);
  };

  const handleToggleNotifications = () => {
    if (isNotificationsPanelOpen) {
      document.getElementsByTagName("body")[0].classList.remove("no-scroll");
    } else {
      document.getElementsByTagName("body")[0].classList.add("no-scroll");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
    toggleToggleNotificationsPanel(!isNotificationsPanelOpen);
  };

  const refetchNotifications = () => {
    notificationsFetcher.load("/profile/my-notifications");
  };

  let hasNewNotifications = false;
  let minNotificationDate = null;
  if (notifications && Array.isArray(notifications)) {
    minNotificationDate = notifications
      .map((notification) => new Date(notification.created_at))
      .reduce(
        (minDate, currentDate) =>
          currentDate < minDate ? currentDate : minDate,
        new Date()
      )
      .toISOString();
    if (notifications.filter(({ read }) => !read).length > 0) {
      hasNewNotifications = true;
    } else {
      hasNewNotifications = false;
    }
  }

  return (
    <div className="lg:px-0 h-full">
      <ImageModalContext.Provider
        value={useMemo(
          () => ({
            imageURL,
            setImageURL,
          }),
          [imageURL, setImageURL]
        )}
      >
        {imageURL && (
          <div className="absolute w-full h-max top-0 bg-gradient-to-t bg-[rgba(0,0,0,0.65)] z-[100]">
            <img
              src={imageURL}
              className="mt-[7.5vh] lg:mt-[5vh] mx-auto lg:max-w-[90vw]"
              alt="Zoomed in"
            />
            <Button
              onClick={() => setImageURL(undefined)}
              className="bg-bg-dark dark:bg-grey-dark !text-[#fff] fixed bottom-8 z-50 lg:bottom-[unset] lg:top-[5rem] lg:border lg:border-[#888] left-4 lg:left-6"
            >
              Close
            </Button>
          </div>
        )}
        {!isMenuOpen && (
          <MobileHeader
            isLoggedIn={isLoggedIn}
            toggleMenu={handleToggleMenu}
            toggleNotificationsPanel={handleToggleNotifications}
            notifications={hasNewNotifications}
          />
        )}
        {isMenuOpen && (
          <MobileMenu
            toggleMenu={handleToggleMenu}
            isLoggedIn={isLoggedIn}
            toggleNotificationsPanel={handleToggleNotifications}
            notifications={hasNewNotifications}
          />
        )}
        <SidebarNav
          isLoggedIn={isLoggedIn}
          notifications={hasNewNotifications}
          toggleNotificationsPanel={handleToggleNotifications}
        />
        <main className="lg:ml-[17.375rem] lg:p-12 box-border">
          <LoadingIndicator />
          <div className="min-h-[85vh]">
            <Outlet />
          </div>
          <Footer />
        </main>
        {isNotificationsPanelOpen && (
          <NotificationsDrawer
            toggleNotificationsPanel={handleToggleNotifications}
            refetchNotifications={refetchNotifications}
            _notifications={
              notifications as GetMyNotificationsQuery["party_notifications"]
            }
            minNotificationDate={minNotificationDate}
          />
        )}
      </ImageModalContext.Provider>
    </div>
  );
}
